import { create } from 'zustand';
import { Resource } from '@setvi/shared/interfaces';

import type { ShoppingCartData } from '../api/shopping-cart';
import type { PrintOrderItem } from '../types';

interface PrintOrderStore {
  items: PrintOrderItem[];
  /** Server cart session; empty until the first successful ShoppingCart call. */
  sessionId: string | null;
  /** Server cart id (ShoppingCart `Data.Id`); used for the DELETE call. */
  cartId: number | null;
  /**
   * Number of in-flight server cart mutations (sync/delete/checkout), across
   * ALL `usePrintOrderCart` instances. Each instance has its own React Query
   * mutation, so per-instance `isLoading` flags can't see each other — this
   * counter is what disables concurrent cart actions globally.
   */
  pendingMutations: number;
  beginMutation: () => void;
  endMutation: () => void;
  /**
   * Commit a successful server sync: replace the items and remember what the
   * ShoppingCart API returned. The API orchestration lives in
   * `usePrintOrderCart` (React Query); this only stores the result.
   */
  applySync: (items: PrintOrderItem[], cart: ShoppingCartData) => void;
  /** Reset the cart locally after the server cart is deleted / cancelled. */
  resetCart: () => void;
  /** Hydrate the cart from the server's active cart. */
  hydrateActiveCart: (
    items: PrintOrderItem[],
    cart: ShoppingCartData | null
  ) => void;
}

export const createPrintOrderItem = (resource: Resource): PrintOrderItem => ({
  resource,
  addQr: false,
  qrNumbers: '',
  addCover: false,
  coverTemplateId: null
});

// What the store keeps of a server cart response.
const fromCart = (cart: ShoppingCartData | null) => ({
  sessionId: cart?.SessionId ?? null,
  cartId: cart?.Id ?? null
});

export const usePrintOrderStore = create<PrintOrderStore>()(set => ({
  items: [],
  ...fromCart(null),
  pendingMutations: 0,

  applySync: (items, cart) => set({ items, ...fromCart(cart) }),
  resetCart: () => set({ items: [], ...fromCart(null) }),
  hydrateActiveCart: (items, cart) => set({ items, ...fromCart(cart) }),
  beginMutation: () =>
    set(state => ({ pendingMutations: state.pendingMutations + 1 })),
  endMutation: () =>
    set(state => ({
      pendingMutations: Math.max(0, state.pendingMutations - 1)
    }))
}));

// Selector helper – used as a hook so components re-render on change.
export const usePrintOrderCount = () =>
  usePrintOrderStore(state => state.items.length);
